<template>
  <div
    v-if="finalRow"
    class="loan-status-timeline-row"
  >
    <div class="spacer">
      <div
        class="inner-row"
      >
        <span
          class="circle-star"
        >
          <img
            id="final-row-star"
            src="~images/svg/white-star.svg"
          >
        </span>
        <span
          id="final-row-message"
          v-html="translate('final_row_message')"
        />
      </div>
    </div>
  </div>
  <div
    v-else
    :class="payment.status"
    class="loan-status-timeline-row"
  >
    <div
      class="spacer"
    >
      <div

        class="inner-row"
      >
        <span
          class="timeline-installment-number"
          v-html="formatInstallmentNumber(payment.installment_number)"
        />
        <div
          class="left-align"
          v-html="translate('date')"
        />
        <div
          class="right-align date"
          v-html="formatDate(payment.installment_due_date)"
        />
      </div>
      <div
        class="inner-row"
      >
        <div
          class="left-align"
          v-html="translate('original_debt')"
        />
        <div
          v-if="payment.status !== 'paid_off'"
          class="right-align amount"
          v-html="formatInstallmentAmount(payment.installment_amount, payment.status)"
        />
        <div
          v-else
          class="right-align status"
          v-html="formatStatus(payment)"
        />
      </div>
      <div
        v-if="payment.status == 'defaulted'"
        class="inner-row"
      >
        <div
          class="left-align"
          v-html="translate('outstanding_amount')"
        />
        <div
          class="right-align"
          v-html="formatCurrentDebt(payment.outstanding_amount)"
        />
      </div>
      <div
        v-if="showThirdInnerRow(payment.status)"
        class="inner-row"
      >
        <div
          class="left-align"
        >
          <template v-if="payment.status === 'next'">
            <span
              id="next-payment-message"
              v-html="translate('next_payment_message')"
            />
          </template>
          <template
            v-if="payment.status === 'defaulted'"
          >
            <span v-html="translate('outstanding_amount')" />
          </template>
        </div>
        <div v-if="payment.status === 'under_payment_plan'">
          <img
            src="~images/svg/MagnifyingGlassDocument.svg"
            class="open-payment-plan-icon"
            alt="Payment Plan Details"
            @click="onPaymentPlanClicked(payment.payment_plan_id, payment.installment_number)"
          >
          <span
            class="payment-plan-link"
            @click="onPaymentPlanClicked(payment.payment_plan_id, payment.installment_number)"
            v-html="formatStatus(payment)"
          />
        </div>
        <div
          v-else
          class="right-align status"
          v-html="formatStatus(payment)"
        />
      </div>
      <div
        v-if="payment.status == 'defaulted'"
        class="inner-row"
      >
        <div
          class="left-align"
        />
        <div
          class="right-align"
        >
          <span
            class="status"
            v-html="translate(payment.status)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { format } from 'date-fns-tz';
import brazilDateFormat from '../filters/brazilDateFormat';
import currencyReals from '../filters/currencyReals';

export default {
  props: {
    payment: {
      type:     Object,
      required: false,
      default:  null,
    },
    finalRow: {
      type:    Boolean,
      default: false,
    },
  },
  data() {
    return {
      statusesWithThirdRow: ['under_payment_plan', 'next', 'pending', 'unpaid'],
    };
  },
  methods: {
    formatDate(dateString) {
      const date = new Date(dateString);
      const utc = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
      const formattedDateString = format(utc, 'yyyy-MM-dd');
      const options = { delimiter: '/' };
      return brazilDateFormat(formattedDateString, options);
    },
    formatInstallmentAmount(amount, status) {
      if (status === 'paid_off') {
        return '<hr class="no-value" />';
      }
      return currencyReals(amount);
    },
    formatCurrentDebt(amount) {
      if (typeof amount === 'undefined') {
        return '';
      }
      return currencyReals(amount);
    },
    formatStatus(payment) {
      switch (payment.status) {
        case 'paid_off':
          return this.translate('paid_off');
        case 'under_payment_plan':
          return this.translate('under_payment_plan') + this.activePaymentPlanSuffix(payment.active_payment_plan_number);
        case 'defaulted':
          return this.translate('defaulted');
        case 'pending':
          return this.translate('pending');
        case 'unpaid':
          return this.translate('pending');
        case 'next':
          return this.translate('pending');
        default:
          return '';
      }
    },
    formatInstallmentNumber(numString) {
      return Number(numString);
    },
    translate(key) {
      return this.$t(`components.loan_status_timeline_row.${key}`);
    },
    showThirdInnerRow(status) {
      return this.statusesWithThirdRow.includes(status);
    },
    onPaymentPlanClicked(paymentPlanId, installmentNumber) {
      this.$emit('openPaymentPlan', paymentPlanId, installmentNumber);
    },
    activePaymentPlanSuffix(int) {
      if (typeof int === 'undefined' || int === null || int < 1 || int > 26) {
        return '';
      }
      return ` - ${String.fromCharCode(96 + int).toUpperCase()}`; // A, B, C ...
    },
  },
};
</script>
<style lang="scss" scoped>
  .loan-status-timeline-row {
    display: flex;
    flex-direction: column;
    margin-left: 1.4em;
    font-weight: bold;
    color: black;
    .spacer {
      margin-bottom: 1em;
    }
    &.paid_off {
        color: rgb(56, 125, 35);
        border-left: 3px solid rgb(56, 125, 35);
        .timeline-installment-number {
          color: white;
          background-color: rgb(56, 125, 35);
        }
      }
      &.under_payment_plan {
        font-weight: bold;
        border-left: 3px solid rgb(45, 107, 148);
        .spacer {
          background-color: rgb(230, 239, 252);
          .timeline-installment-number {
            color: white;
            background-color: rgb(45, 107, 148);
          }
        }
        .date, .amount {
          text-decoration: line-through;
        }
      }
      &.defaulted {
        font-weight: bold;
        border-left: 3px solid rgb(196, 41, 28);
        .spacer {
          background-color: rgb(248, 232, 230);
          .timeline-installment-number {
            color: white;
            background-color: rgb(196, 41, 28);
          }
        }
        .date, .status {
          color: rgb(198, 44, 33);
        }
      }
      &.next {
        font-weight: bold;
        border-left: 3px solid rgb(237, 193, 66);
        .timeline-installment-number {
        color: black;
        background-color: rgb(237, 193, 66);
        }
      }
      &.pending, &.unpaid {
        color: rgb(116, 119, 124);
        border-left: 3px solid rgb(116, 119, 124);
        .timeline-installment-number {
          color: white;
          background-color: rgb(116, 119, 124);
        }
        .amount {
          color: black;
        }
      }
  }
  .inner-row {
    display: flex;
    flex-direction: row;
    position: relative;
    justify-content: space-between;
    padding-top: 0.5em;
    padding-left: 2em;
    padding-right: 0.5em;
    padding-bottom: 0.5em;
  }
  .left-align {
    text-align: left;
    max-width: 50%;
    color: black;
  }
  .right-align {
    text-align: right;
    max-width: 50%;
  }
  .timeline-installment-number {
    box-sizing: content-box;
    border-radius: 50%;
    text-align: center;
    font-size: 1.5em;
    width: 1.6em;
    height: 1.6em;
    line-height: 1.4em;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: -0.1em;
    left: calc(-0.8em - 1px);
  }
  .payment-plan-link {
    color: rgb(45, 107, 148);
    font-size: 1.1em;
    text-decoration: underline;
    &:hover {
      cursor: pointer;
    }
  }
  #next-payment-message {
    font-weight: 200;
    font-size: 0.75em;
    line-height: 1.1;
    display: block;
  }
  .circle-star {
    box-sizing: content-box;
    border-radius: 50%;
    text-align: center;
    font-size: 1.5em;
    width: 1.6em;
    height: 1.6em;
    line-height: 1.4em;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgb(116, 119, 124);
    color: white;
    position: absolute;
    top: -0.1em;
    left: calc(-0.8em + 1px);
  }
  #final-row-message {
    color: rgb(116, 119, 124);
  }
</style>
